import { useAppraisalFlow } from '../useAppraisalFlow'
import { useAppraisalHandoff } from '../useAppraisalHandoff'
import { useAppraisalTracking } from '../useAppraisalTracking'
import useTranslation from 'next-translate/useTranslation'
import { Button } from '@/components/Button'
import AppraisalLanding from './AppraisalLanding'
import ErrorView from './ErrorView'
import HandoffView from './HandoffView'
import LoadingView from './LoadingView'
import QuestionList from './QuestionList'
import RejectionView from './RejectionView'
import RequestView from './RequestView'
import ResultView from './ResultView'
import SummaryPanel from './SummaryPanel'

export default function AppraisalFlow() {
  const { t } = useTranslation('wertCheck')
  const flow = useAppraisalFlow()
  const handoff = useAppraisalHandoff()
  useAppraisalTracking(flow)

  const showSummary = flow.phase !== 'landing'

  return (
    <div className="flex flex-col md:grid md:grid-cols-[2fr_1fr] gap-6 w-full max-w-[900px] mx-auto px-4 py-8">
      <main className="flex flex-col gap-6">
        {flow.phase === 'landing' && (
          <AppraisalLanding onSubmit={flow.submitDescription} />
        )}

        {flow.phase === 'loadingQuestions' && (
          <LoadingView message={t('loading_questions')} />
        )}

        {flow.phase === 'loadingEstimate' && (
          <LoadingView message={t('loading_estimate')} />
        )}

        {flow.phase === 'questions' && (
          <QuestionList
            questions={flow.questions}
            answers={flow.answers}
            canSubmit={flow.canSubmitAnswers}
            isFinalStep={flow.isFinalStep}
            onAnswerChange={flow.setAnswer}
            onSubmit={flow.submitAnswers}
          />
        )}

        {flow.phase === 'result' && flow.valuation && (
          <ResultView
            amountEur={flow.valuation.payoutAmount}
            disclaimer={flow.valuation.disclaimer}
            adjustQuestions={flow.questions}
            answers={flow.answers}
            canRecalculate={flow.canSubmitAnswers}
            onAnswerChange={flow.setAnswer}
            onRecalculate={flow.submitAnswers}
            onCtaClick={
              flow.valuation.checkout
                ? () => {
                    if (flow.valuation) handoff.startHandoff(flow.valuation)
                  }
                : undefined
            }
            isSubmitting={handoff.isSubmitting}
          />
        )}

        {flow.phase === 'rejected' && (
          <RejectionView note={flow.rejectionMessage} onReset={flow.reset} />
        )}

        {flow.phase === 'handoff' && flow.handoff && (
          <HandoffView
            target={flow.handoff.target}
            message={flow.handoff.message}
            vehicle={flow.handoff.vehicle}
            onReset={flow.reset}
          />
        )}

        {flow.phase === 'request' && flow.request && (
          <RequestView
            message={flow.request.message}
            suggestedAmount={flow.request.suggestedAmount}
            minAmount={flow.request.minAmount}
            maxAmount={flow.request.maxAmount}
            onSubmit={(amount) => {
              if (flow.request) handoff.startRequest(flow.request, amount)
            }}
            onReset={flow.reset}
            isSubmitting={handoff.isSubmitting}
          />
        )}

        {flow.phase === 'error' && (
          <ErrorView onRetry={flow.retry} onReset={flow.reset} />
        )}

        {(flow.phase === 'questions' || flow.phase === 'result') && (
          <Button
            type="button"
            variant="link"
            className="self-center"
            onClick={flow.reset}
          >
            {t('reset_new_item')}
          </Button>
        )}
      </main>

      {showSummary && (
        <SummaryPanel
          description={flow.text}
          answeredQuestions={flow.submittedAnswers}
          itemLabel={flow.productName}
        />
      )}
    </div>
  )
}
